import { UserAvatar } from "@/components/ui/stacked-avatars";
import type { ActivityClientConfig } from "@/lib/activities/registry";
import { useActivity, type ActivityType } from "@/lib/api/activities";
import { usePostHogEvent } from "@/lib/posthog";
import { colorsV2 } from "@somethings-ui/core";
import { Assets, Scribbles } from "@somethings-ui/core/assets";
import { useState } from "react";

const ICON_BY_TYPE: Partial<Record<ActivityType, string>> = {
  hot_takes: Assets.hotTakesIcon,
  deep_thoughts: Assets.deepThoughtsIcon,
  connect_moment: Assets.connectMomentIcon,
};

const DEFAULT_CARD_STYLE = {
  background: "linear-gradient(180deg, #322964 0%, #1D1F46 100%)",
  cloud: Scribbles.purpleCloudTilted,
  cloudFullBleed: false,
};

const CARD_STYLE_BY_TYPE: Partial<
  Record<ActivityType, typeof DEFAULT_CARD_STYLE>
> = {
  connect_moment: {
    background: colorsV2.brand.periwinkleDark,
    cloud: Scribbles.periwinkleCloud,
    cloudFullBleed: true,
  },
};

interface TeenSentActivityEntryProps {
  ResponderDialog: NonNullable<ActivityClientConfig["ResponderDialog"]>;
  activityType: ActivityType;
  entityId: string;
  teenName: string;
  label: string;
  teenAvatarUrl: string | null;
  mentorAvatarUrl: string | null;
}

export function TeenSentActivityEntry({
  ResponderDialog,
  activityType,
  entityId,
  teenName,
  label,
  teenAvatarUrl,
  mentorAvatarUrl,
}: TeenSentActivityEntryProps) {
  const icon = ICON_BY_TYPE[activityType];
  const cardStyle = CARD_STYLE_BY_TYPE[activityType] ?? DEFAULT_CARD_STYLE;
  const [isResponderOpen, setIsResponderOpen] = useState(false);
  const { data: activity } = useActivity(entityId);
  const { dispatchEvent } = usePostHogEvent("activity.mentor");

  const openResponder = () => {
    dispatchEvent("activity.mentor.responder_opened", {
      entityId,
      activityType,
      seconds_since_teen_sent: activity
        ? Math.round(
            (Date.now() - new Date(activity.createdAt).getTime()) / 1000,
          )
        : null,
    });
    setIsResponderOpen(true);
  };

  return (
    <div className="flex flex-col items-center gap-3 py-3">
      <div className="flex items-center gap-2">
        {icon && (
          <img
            src={icon}
            alt=""
            aria-hidden
            className="h-7 w-7 shrink-0 object-contain"
          />
        )}
        <span className="text-sm text-secondary">
          {teenName} sent {label}
        </span>
      </div>

      <button
        type="button"
        onClick={openResponder}
        aria-label={`Answer ${teenName}'s ${label}`}
        className="relative w-[400px] overflow-hidden rounded-2xl p-4 text-left shadow-small transition-opacity hover:opacity-90"
        style={{ background: cardStyle.background }}
      >
        <img
          src={cardStyle.cloud}
          alt=""
          aria-hidden
          className={
            cardStyle.cloudFullBleed
              ? "pointer-events-none absolute inset-0 h-full w-full object-cover opacity-95"
              : "pointer-events-none absolute left-0 right-0 top-0 w-full object-contain"
          }
          style={
            cardStyle.cloudFullBleed
              ? { transform: "rotate(15deg) scale(2)" }
              : undefined
          }
        />
        <div className="relative flex flex-col gap-4">
          <div className="flex items-center">
            <div className="-mr-2.5 rounded-full border border-white">
              <UserAvatar
                imageUrl={teenAvatarUrl ?? undefined}
                name={teenName || "Teen"}
                size={32}
                showBorder={false}
              />
            </div>
            <div className="rounded-full border border-white">
              <UserAvatar
                imageUrl={mentorAvatarUrl ?? undefined}
                name="You"
                size={32}
                showBorder={false}
              />
            </div>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-xl font-bold tracking-tight text-white">
              {label}
            </span>
            <span className="font-mono text-xs text-white">
              Waiting for you
            </span>
          </div>
        </div>
      </button>

      {isResponderOpen && (
        <ResponderDialog
          isOpen
          entityId={entityId}
          teenName={teenName}
          onClose={() => setIsResponderOpen(false)}
        />
      )}
    </div>
  );
}
